import { QuestionnaireQuestions } from '@getpopsure/qnr-framework';
import { GeneralQuestion } from 'features/generalQuestionnaire/model';
import { ZDateStringSchema } from 'shared/models/validations';
import { capitalizeSingleWord } from 'shared/util/capitalizeSingleWord';

export const getReviewValueByType = <
  Questionnaire extends QuestionnaireQuestions
>({
  questionId,
  questionnaireAnswers,
}: {
  questionId: keyof Questionnaire;
  questionnaireAnswers: Questionnaire;
}) => {
  const answer = questionnaireAnswers[questionId];

  if (!answer) {
    return null;
  }

  if (Array.isArray(answer)) {
    return {
      type: 'Array',
      key: questionId as string,
      transform: (s: string) =>
        s.toUpperCase() === s
          ? capitalizeSingleWord(s.replaceAll('_', ' '))
          : s,
    };
  }

  if (ZDateStringSchema.safeParse(answer).data) {
    return {
      type: 'Date',
      format: 'DD MMM YYYY',
      key: questionId,
    };
  }

  if (
    typeof answer === 'number' &&
    typeof questionId === 'string' &&
    questionId.toLowerCase().includes('price')
  ) {
    return {
      type: 'Currency',
      key: questionId as string,
    };
  }

  return { type: 'String', key: questionId as string };
};

export const getReviewValue = <Questionnaire extends QuestionnaireQuestions>({
  questions,
  questionId,
  questionnaireAnswers,
}: {
  questions: GeneralQuestion;
  questionId: keyof Questionnaire;
  questionnaireAnswers: Questionnaire;
}) => {
  const question = questions.find((q) => q.id === questionId);
  if (!question) {
    return getReviewValueByType({
      questionId,
      questionnaireAnswers,
    });
  }

  switch (question.type) {
    case 'ADDRESS':
      return {
        type: 'Strings',
        key: 'address',
        keys: ['street', 'houseNumber', 'postcode', 'city'],
      };
    case 'NAME':
      return {
        type: 'Strings',
        key: question.id,
        keys: ['firstName', 'lastName'],
      };
    case 'DATE':
      return {
        type: 'Date',
        format: 'DD MMM YYYY',
        key: question.id,
      };

    case 'CHECKBOX':
    case 'RADIO':
      return {
        type: 'String',
        key: question.id,
        valueMap: question.props.mapValue?.title ?? question.props.mapValue,
      };

    case 'BOOLEAN':
      return {
        type: 'Boolean',
        key: question.id,
      };

    case 'COUNTRY_SINGLE':
      return {
        type: 'Strings',
        key: question.id,
        keys: ['name'],
      };

    case 'COUNTRY_MULTI':
      return { type: 'Array', key: question.id, pick: ['name'] };

    case 'QUOTEPAGE':
      return {
        type: 'Strings',
        key: question.id,
        keys: ['planId'],
      };
    default: {
      const value = getReviewValueByType({
        questionId,
        questionnaireAnswers,
      });

      return value ?? { type: 'String', key: questionId };
    }
  }
};
